import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Mail, Send, X, Plus, Save } from "lucide-react";

export const SmsDigestSettings = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["sms-digest"], queryFn: async () => (await api.get("/sms/digest")).data });
  const [form, setForm] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  useEffect(() => { if (data && !form) setForm({ enabled: data.enabled, recipients: data.recipients }); }, [data, form]);
  const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");

  const save = useMutation({
    mutationFn: async (f) => (await api.post("/sms/digest", f)).data,
    onSuccess: (d) => { setForm({ enabled: d.enabled, recipients: d.recipients }); toast.success("Digest settings saved"); qc.invalidateQueries({ queryKey: ["sms-digest"] }); },
    onError: err,
  });
  const sendNow = useMutation({
    mutationFn: async () => (await api.post("/sms/digest/send-now")).data,
    onSuccess: (d) => d.sent_to?.length ? toast.success(`Digest sent to ${d.sent_to.join(", ")}`) : toast.error("Digest email could not be sent"),
    onError: err,
  });
  const add = () => {
    const e = newEmail.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(e)) { toast.error("Enter a valid email"); return; }
    if (!form.recipients.includes(e)) setForm({ ...form, recipients: [...form.recipients, e] });
    setNewEmail("");
  };
  if (!form) return null;
  const p = data?.preview;

  return (
    <div data-testid="sms-digest-card" className="bg-white border border-slate-200 rounded-xl shadow-sm mb-6 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-cyan-500" />
          <h3 className="font-heading font-bold text-slate-900">Daily SMS Digest Email</h3>
          <span className="text-xs text-slate-400">· every morning ~8 AM Central, covers yesterday</span>
        </div>
        <div className="flex items-center gap-2.5">
          <span className="text-sm font-semibold text-slate-700">Enabled</span>
          <Switch data-testid="sms-digest-toggle" checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
        </div>
      </div>
      <div className="p-6 space-y-4">
        <div className="flex flex-wrap gap-2" data-testid="sms-digest-recipients">
          {form.recipients.map((r) => (
            <span key={r} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">
              {r}
              <button data-testid={`sms-digest-remove-${r}`} onClick={() => setForm({ ...form, recipients: form.recipients.filter((x) => x !== r) })}
                className="text-slate-400 hover:text-red-600"><X className="h-3 w-3" /></button>
            </span>
          ))}
          {form.recipients.length === 0 && <span className="text-xs text-slate-400">No recipients — add at least one.</span>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Input data-testid="sms-digest-email-input" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") add(); }} placeholder="Add recipient email" className="h-9 max-w-xs" />
          <Button data-testid="sms-digest-add" variant="outline" className="h-9" onClick={add}><Plus className="h-4 w-4 mr-1.5" />Add</Button>
          <Button data-testid="sms-digest-save" className="h-9 bg-slate-900 hover:bg-slate-800 text-white" disabled={save.isPending} onClick={() => save.mutate(form)}>
            <Save className="h-4 w-4 mr-1.5" />Save
          </Button>
          <Button data-testid="sms-digest-send-now" variant="outline" className="h-9" disabled={sendNow.isPending} onClick={() => sendNow.mutate()}>
            <Send className="h-4 w-4 mr-1.5" />{sendNow.isPending ? "Sending…" : "Send digest now"}
          </Button>
        </div>
        {p && (
          <div data-testid="sms-digest-preview" className="text-xs text-slate-500">
            Yesterday ({p.date_label}): <b className="text-slate-800">{p.totals.sent}</b> sent · <b className="text-red-600">{p.totals.failed}</b> failed/blocked · <b className="text-amber-700">{p.optouts}</b> opt-outs · <b className="text-emerald-700">{p.resubs}</b> re-subscribes
            {data?.last_sent_at ? ` · Last scheduled digest ${fmtDateTime(data.last_sent_at)}` : ""}
          </div>
        )}
      </div>
    </div>
  );
};
